import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './pages/DashboardLayout';
import SheepListPage from './pages/SheepListPage';
import SheepDetailPage from './pages/SheepDetailPage';
import PublicSheepPage from './pages/PublicSheepPage';
import UsersPage from './pages/UsersPage';
import FullPedigreePage from './pages/FullPedigreePage';

dayjs.locale('ru');

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spin style={{ display: 'block', marginTop: 200 }} size="large" />;
  return user ? <>{children}</> : <Navigate to="/login" replace />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spin style={{ display: 'block', marginTop: 200 }} size="large" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/my-sheep" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <Spin style={{ display: 'block', marginTop: 200 }} size="large" />;

  const homeRedirect = '/';

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={homeRedirect} replace /> : <LoginPage />} />

      {/* Public QR routes */}
      <Route path="/sheep/:id" element={<PublicSheepPage />} />
      <Route path="/sheep/:id/pedigree" element={<FullPedigreePage />} />

      {/* Protected dashboard */}
      <Route element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
        {/* All users */}
        <Route path="/my-sheep" element={<SheepListPage mine />} />
        <Route path="/sheep/:id/detail" element={<SheepDetailPage />} />
        <Route path="/sheep/:id/full-pedigree" element={<FullPedigreePage />} />

        {/* All users: all sheep */}
        <Route path="/" element={<SheepListPage />} />
        {/* Admin only */}
        <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
      </Route>

      <Route path="*" element={<Navigate to={user ? homeRedirect : '/login'} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ConfigProvider locale={ruRU} theme={{
      token: { colorPrimary: '#764ba2' },
    }}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ConfigProvider>
  );
}
